import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import {
  audioModelsByKind,
  isSoundKind,
  pickAudioModel,
  type AudioModelKind,
  type AudioVoiceOption,
} from '../../lib/audioModelCatalog';

export type AudioModelPickerValue = { voiceModelId: string; voiceId: string };

type Props = {
  kind: AudioModelKind;
  value: AudioModelPickerValue;
  onChange: (next: AudioModelPickerValue) => void;
  /** Hide models that need a source track when there is none to offer. */
  allowSourceModels?: boolean;
};

function modelKindBadge(kind?: string) {
  if (kind === 'music') return 'Música';
  if (kind === 'sfx') return 'Som';
  return 'Voz';
}

/** Sound models of one kind (voice / music / effect) plus, for voices, the voice list with official previews. */
export default function AudioModelPicker({ kind, value, onChange, allowSourceModels = true }: Props) {
  const catalog = audioModelsByKind(kind).filter((m) => allowSourceModels || !m.requiresSourceAudio);
  const model = pickAudioModel(value.voiceModelId, kind);
  const soundMode = isSoundKind(kind);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const previewRef = useRef<HTMLAudioElement | null>(null);

  const stopPreview = useCallback(() => {
    previewRef.current?.pause();
    previewRef.current = null;
    setPlayingId(null);
  }, []);

  useEffect(() => () => stopPreview(), [stopPreview]);

  const playPreview = (voice: AudioVoiceOption) => {
    if (!voice.previewUrl) return;
    if (playingId === voice.id) {
      stopPreview();
      return;
    }
    previewRef.current?.pause();
    const el = new Audio(voice.previewUrl);
    previewRef.current = el;
    setPlayingId(voice.id);
    el.onended = () => setPlayingId((id) => (id === voice.id ? null : id));
    el.onerror = () => setPlayingId((id) => (id === voice.id ? null : id));
    void el.play().catch(() => setPlayingId(null));
  };

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        {catalog.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              onChange({ voiceModelId: opt.id, voiceId: opt.defaultVoice });
              stopPreview();
            }}
            style={{
              textAlign: 'left',
              border: model.id === opt.id ? '1px solid #818cf8' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              background: model.id === opt.id ? 'rgba(99,102,241,0.18)' : 'rgba(255,255,255,0.03)',
              color: '#fff',
              padding: '8px 10px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 650 }}>{opt.label}</div>
              {soundMode && (
                <span style={{
                  fontSize: '0.62rem',
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: opt.kind === 'music' ? 'rgba(167,139,250,0.22)' : 'rgba(34,211,238,0.18)',
                  color: opt.kind === 'music' ? '#ddd6fe' : '#a5f3fc',
                }}>
                  {modelKindBadge(opt.kind)}
                </span>
              )}
            </div>
            <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              {soundMode
                ? `${opt.creditsPerGeneration ?? opt.creditsPer1kChars} cr / geração${opt.requiresSourceAudio ? ' · em cima da locução' : ''}`
                : `${opt.creditsPer1kChars} cr / 1k caracteres`}
            </div>
          </button>
        ))}
      </div>
      {kind === 'voice' && model.voices.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Voz</div>
          <div
            style={{
              maxHeight: 196,
              overflowY: 'auto',
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(0,0,0,0.22)',
            }}
          >
            {model.voices.map((voice) => {
              const selected = voice.id === value.voiceId;
              const playing = playingId === voice.id;
              return (
                <div
                  key={voice.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    background: selected ? 'rgba(99,102,241,0.18)' : 'transparent',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onChange({ voiceModelId: model.id, voiceId: voice.id })}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: 'left',
                      border: 'none',
                      background: 'transparent',
                      color: selected ? '#e9d5ff' : 'rgba(255,255,255,0.88)',
                      fontSize: '0.78rem',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      padding: '4px 2px',
                    }}
                  >
                    {voice.label}
                  </button>
                  <button
                    type="button"
                    disabled={!voice.previewUrl}
                    onClick={() => playPreview(voice)}
                    aria-label={playing ? `Pausar ${voice.label}` : `Ouvir ${voice.label}`}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      border: '1px solid rgba(255,255,255,0.14)',
                      background: playing ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.06)',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: voice.previewUrl ? 'pointer' : 'default',
                      opacity: voice.previewUrl ? 1 : 0.35,
                      flexShrink: 0,
                    }}
                  >
                    {playing ? <Pause size={13} /> : <Play size={13} />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
